import { useCallback, useEffect, useRef, useState } from "react";

/** How far below the sticky header a section's top may be and still count as reached. */
const REACHED_MARGIN_PX = 48;
/** How long a clicked item stays the active one while the page scrolls to it. */
const PIN_MS = 1200;

/**
 * Which of the given sections the reader is in.
 *
 * The answer is the last section whose top has passed under the header, so the
 * sections that have no nav item of their own count as part of the one before
 * them. Above the first section nothing is active.
 *
 * Clicking a nav item scrolls smoothly past everything in between, and reading
 * the position during that scroll would light up each item in turn. `pin`
 * names the destination as active straight away and holds it until the scroll
 * has had time to arrive.
 */
export function useActiveSection(sectionIds: string[], isEnabled = true) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const pinnedRef = useRef<string | null>(null);
  const pinTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const key = sectionIds.join("|");

  useEffect(() => {
    if (!isEnabled) {
      setActiveId(null);
      return;
    }
    const ids = key.split("|").filter(Boolean);
    let frame = 0;

    const read = () => {
      frame = 0;
      if (pinnedRef.current) return;
      const header = document.querySelector("header");
      const line = (header?.getBoundingClientRect().bottom ?? 0) + REACHED_MARGIN_PX;
      let current: string | null = null;
      for (const id of ids) {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= line) current = id;
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };

    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [key, isEnabled]);

  useEffect(() => () => clearTimeout(pinTimerRef.current), []);

  const pin = useCallback((id: string) => {
    pinnedRef.current = id;
    setActiveId(id);
    clearTimeout(pinTimerRef.current);
    pinTimerRef.current = setTimeout(() => {
      pinnedRef.current = null;
    }, PIN_MS);
  }, []);

  return { activeId, pin };
}

export default useActiveSection;
